/**
 * Agency Billing Types
 * Types for managing agency seat billing and agent subscriptions
 */

export enum SeatStatus {
  ACTIVE = 'active',
  GRACE_PERIOD = 'grace_period',
  REMOVED = 'removed',  // Backend uses 'removed' not 'canceled'
  PENDING = 'pending',
  NONE = 'none',  // For users with no subscription
}

export enum PlanType {
  BASIC = 'basic',
  FULL_FLEDGED = 'full_fledged',
}

export enum PaymentSource {
  AGENCY = 'agency',
  SELF = 'self',
}

export interface AgencyBillingSeat {
  id: string;
  userId: string;
  agencyId: string;
  userName: string;
  userEmail: string;
  planType: PlanType;
  paymentSource: PaymentSource;
  status: SeatStatus;
  monthlyPrice: number;
  startDate: string;
  endDate?: string;
  gracePeriodEnd?: string;
  lastBillingDate?: string;
  nextBillingDate?: string;
  canceledAt?: string;
  cancelReason?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AgencyBillingSummary {
  agencyId: string;
  agencyName: string;
  totalAgents: number;
  agencyPaidAgents: number;
  selfPaidAgents: number;
  activeSeats: number;
  gracePeriodSeats: number;
  canceledSeats: number;
  totalMonthlyCost: number;
  agencyMonthlyCost: number;
  selfPaidMonthlyCost: number;
  basicPlanCount: number;
  fullFledgedPlanCount: number;
  nextBillingDate?: string;
  lastUpdated: string;
}

export interface ProrationPreview {
  currentCharge: number;
  newCharge: number;
  prorationAmount: number;
  isUpgrade: boolean;
  effectiveDate: string;
  nextBillingDate: string;
  description: string;
}

export interface ChangePlanRequest {
  agencyId: string;
  userId: string;
  newPlan: PlanType;
}

export interface TogglePaymentSourceRequest {
  agencyId: string;
  userId: string;
  newPaymentSource: PaymentSource;
}

export interface AddSeatRequest {
  agencyId: string;
  userId: string;
  planType: PlanType;
  paymentSource: PaymentSource;
}

export interface RemoveSeatRequest {
  agencyId: string;
  userId: string;
  reason?: string;
}

export interface BillingOperation {
  success: boolean;
  message: string;
  data?: unknown;
  error?: string;
}

export interface GracePeriodInfo {
  isInGracePeriod: boolean;
  daysRemaining: number;
  gracePeriodEnd?: string;
  warningLevel: 'none' | 'info' | 'warning' | 'critical';
}

// Helper type for table display
export interface AgentTableRow extends AgencyBillingSeat {
  gracePeriodInfo?: GracePeriodInfo;
}

// API Response types
export interface GetAgencySeatsResponse {
  seats: AgencyBillingSeat[];
  summary: AgencyBillingSummary;
}

export interface ProrationPreviewResponse {
  proration: ProrationPreview;
}
